import { useTu } from "@src/appV2/i18n/useTu";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Button, Link } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { type FormEvent, useEffect, useRef, useState } from "react";

const OTP_CODE_LENGTH = 8;

interface OtpEntryFormProps {
  email: string;
  loading: boolean;
  errorMessage?: string;
  /** Whether the resend cooldown is currently active. */
  isResendCoolingDown: boolean;
  /** Seconds left on the resend cooldown; shown in the button label. */
  resendSecondsRemaining: number;
  /** Whether the current code can no longer be submitted. */
  isCodeExpired?: boolean;
  /** Whether the current code submit action should be blocked. */
  isSubmitBlocked?: boolean;
  onSubmit: (code: string) => Promise<void>;
  onResend: () => void;
  onBack: () => void;
}

/** Group the raw digits as `1234 5678` for display. */
function formatOtp(rawDigits: string): string {
  if (rawDigits.length <= 4) {
    return rawDigits;
  }
  return `${rawDigits.slice(0, 4)} ${rawDigits.slice(4)}`;
}

/**
 * Code-entry screen shown after the user requests an email OTP. The user types
 * the 8-digit code from the email here, on the same browser that requested it.
 * The input stores raw digits and displays them grouped as `1234 5678`.
 */
export function OtpEntryForm(props: OtpEntryFormProps): React.ReactElement {
  const { tu } = useTu("login");
  const {
    email,
    loading,
    errorMessage,
    isResendCoolingDown,
    resendSecondsRemaining,
    isCodeExpired = false,
    isSubmitBlocked = isCodeExpired,
    onSubmit,
    onResend,
    onBack,
  } = props;

  const [code, setCode] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const isCodeComplete = code.length === OTP_CODE_LENGTH;
  const resendActionLabel = tu("Resend code");
  const errorResendIndex = errorMessage?.indexOf(resendActionLabel) ?? -1;
  const inlineResendLinkSx = {
    backgroundColor: "transparent",
    border: 0,
    color: "primary.main",
    cursor: loading ? "default" : "pointer",
    font: "inherit",
    padding: 0,
    verticalAlign: "baseline",
    "&:disabled": {
      color: "text.disabled",
      cursor: "default",
    },
  };

  useEffect(() => {
    if (!isSubmitBlocked || isResendCoolingDown || loading) {
      return;
    }

    formRef.current?.querySelector<HTMLButtonElement>("[data-resend-code-button]")?.focus();
  }, [isSubmitBlocked, isResendCoolingDown, loading]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!isCodeComplete || isSubmitBlocked) {
      return;
    }
    await onSubmit(code);
  }

  function renderResendLink(variant: "body1" | "body2") {
    return (
      <Link
        component="button"
        type="button"
        variant={variant}
        disabled={loading}
        data-resend-code-button
        sx={inlineResendLinkSx}
        onClick={onResend}
      >
        {resendActionLabel}
      </Link>
    );
  }

  return (
    <Box
      component="form"
      ref={formRef}
      sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
        <Text bold variant="h5" sx={{ color: "base.base" }}>
          {isSubmitBlocked ? tu("Get a new code") : tu("Enter your code")}
        </Text>
      </Box>
      <Text variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
        {tu("We sent an 8-digit code to {{email}}.", { values: { email } })}
        {!isSubmitBlocked && <> {tu("Enter it below to sign in.")}</>}
        <>
          {" "}
          {isResendCoolingDown ? (
            resendSecondsRemaining === 1 ? (
              tu("You can resend the code in 1 second.")
            ) : (
              tu("You can resend the code in {{seconds}} seconds.", {
                values: { seconds: resendSecondsRemaining },
              })
            )
          ) : (
            <>{renderResendLink("body1")}.</>
          )}
        </>
      </Text>
      {!isSubmitBlocked && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
          <Text semibold variant="body2" sx={{ color: "base.muted" }}>
            {tu("Verification code")}
          </Text>
          <TextInput
            fullWidth
            required
            type="text"
            placeholder="0000 0000"
            value={formatOtp(code)}
            disabled={loading}
            autoComplete="one-time-code"
            inputProps={{
              "aria-label": tu("Verification code"),
              inputMode: "numeric",
              pattern: "[0-9 ]*",
              // 8 digits + the grouping space.
              maxLength: OTP_CODE_LENGTH + 1,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "1.5rem",
                letterSpacing: "0.4rem",
                textAlign: "center",
                backgroundColor: "background.standout",
              },
            }}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, "").slice(0, OTP_CODE_LENGTH));
            }}
          />
        </Box>
      )}
      {isDefined(errorMessage) && (
        <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
          {errorResendIndex >= 0 ? (
            <>
              {errorMessage.slice(0, errorResendIndex)}
              {renderResendLink("body2")}
              {errorMessage.slice(errorResendIndex + resendActionLabel.length)}
            </>
          ) : (
            errorMessage
          )}
        </Text>
      )}
      {!isSubmitBlocked && (
        <LoadingButton
          fullWidth
          type="submit"
          variant="primary"
          size="large"
          isLoading={loading}
          disabled={!isCodeComplete}
        >
          {tu("Sign in")}
        </LoadingButton>
      )}
      <Button
        variant="text"
        disabled={loading}
        sx={{ textTransform: "none", color: "primary.main" }}
        onClick={onBack}
      >
        {tu("Go back to login page")}
      </Button>
    </Box>
  );
}
